import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Card, CardContent, TextField, Button, Link,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { reportingCostAPI } from '../services/reportingCostApi';
import { saveBlob } from '../utils/download';

export const NET = '#1976d2';
export const SP = '#2e7d32';
export const EDP = '#7b1fa2';
export const BAD = '#d32f2f';
export const usd = (n, d = 0) => (n == null ? '-' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`);
// A region or month with nothing spent in it (gross and net both $0) is left out of the cost-by-region tables.
export const hasSpend = (r) => Number(r.cost_gross || 0) !== 0 || Number(r.cost_net || 0) !== 0;
export const pct = (n) => (n == null ? '-' : `${Number(n).toFixed(1)}%`);
export const errText = (err, fallback) => err?.response?.data?.detail || fallback;
const monthLabel = (iso, long) => new Date(`${iso.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: long ? 'long' : 'short', year: long ? 'numeric' : '2-digit', timeZone: 'UTC' });

// The last 13 whole months (the current, unfinished month first is left out of the default: the ingest fills a month once it is over).
export function monthOptions(now = new Date()) {
  return Array.from({ length: 13 }, (_, i) => {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
    return { value, label: monthLabel(value, true) };
  });
}

// The months that have data (newest first); falls back to the last whole months when the list cannot be read. Renders `children(options)` once known.
export function WithMonths({ children }) {
  const [options, setOptions] = useState(null);
  useEffect(() => {
    let live = true;
    const fallback = () => monthOptions().slice(1);
    reportingCostAPI.months()
      .then((res) => live && setOptions((res.data || []).length ? res.data.map((value) => ({ value, label: monthLabel(value, true) })) : fallback()))
      .catch(() => live && setOptions(fallback()));
    return () => { live = false; };
  }, []);
  if (!options) return <Loading />;
  return children(options);
}

export function MonthSelect({ value, onChange, options }) {
  return (
    <TextField select size="small" label="Month" value={value} onChange={(e) => onChange(e.target.value)} slotProps={{ select: { native: true }, inputLabel: { shrink: true } }} sx={{ minWidth: 200 }}>
      {(options || monthOptions()).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </TextField>
  );
}

export function Kpi({ label, value, sub, color }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" component="div" sx={{ color }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{sub}</Typography>
      </CardContent>
    </Card>
  );
}

export const KpiGrid = ({ children }) => <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>{children}</Box>;

export function Legend({ items }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
      {items.map(([label, color]) => <Typography key={label} variant="caption"><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: color, mr: 0.5 }} />{label}</Typography>)}
    </Box>
  );
}

// Net cost, then the savings plan saving, then EDP, as segments of the gross cost.
export function CostBar({ gross, net, edp = 0, untagged = false }) {
  const total = Number(gross) || 1;
  const sp = Math.max(Number(gross) - Number(net) - Number(edp), 0);
  const w = (v) => `${Math.max(0, Math.min(100, (Number(v) / total) * 100))}%`;
  return (
    <Tooltip title={`Net ${usd(net)} of gross ${usd(gross)}`}>
      <Box sx={{ display: 'flex', height: 8, minWidth: 80, borderRadius: 0.5, overflow: 'hidden', bgcolor: 'action.hover' }} aria-hidden>
        <Box sx={{ width: w(net), bgcolor: untagged ? BAD : NET }} />
        <Box sx={{ width: w(sp), bgcolor: SP, opacity: 0.7 }} />
        <Box sx={{ width: w(edp), bgcolor: EDP, opacity: 0.7 }} />
      </Box>
    </Tooltip>
  );
}

// Monthly net cost with the saving stacked on top, as one small SVG.
export function TrendChart({ data }) {
  if (!data?.length) return <Typography color="text.secondary">No trend data.</Typography>;
  const W = 600, H = 190, top = 14, bottom = 24, bw = Math.min(40, (W / data.length) * 0.6);
  const max = Math.max(1, ...data.map((m) => Number(m.cost_net || 0) + Number(m.cost_saving ?? m.cost_total_saving ?? 0)));
  const y = (v) => ((v / max) * (H - top - bottom));
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 240 }} role="img" aria-label="Monthly cost trend">
        {data.map((m, i) => {
          const cx = ((i + 0.5) * W) / data.length;
          const net = Number(m.cost_net || 0), sav = Number(m.cost_saving ?? m.cost_total_saving ?? 0);
          return (
            <g key={m.month}>
              <title>{`${monthLabel(m.month)}: net ${usd(net)}, saving ${usd(sav)}`}</title>
              <rect x={cx - bw / 2} y={H - bottom - y(net)} width={bw} height={y(net)} fill={NET} />
              <rect x={cx - bw / 2} y={H - bottom - y(net) - y(sav)} width={bw} height={y(sav)} fill={SP} opacity="0.65" />
              <text x={cx} y={H - bottom - y(net) - y(sav) - 3} textAnchor="middle" fontSize="9" fill="currentColor">{usd(net)}</text>
              <text x={cx} y={H - 8} textAnchor="middle" fontSize="10" fill="currentColor">{monthLabel(m.month)}</text>
            </g>
          );
        })}
      </svg>
      <Legend items={[['Net', NET], ['Savings', SP]]} />
    </Box>
  );
}

export function useLoad(fn, deps) {
  const [state, setState] = useState({ data: null, error: '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);
  useEffect(() => {
    let live = true;
    setState({ data: null, error: '' });
    run().then((d) => live && setState({ data: d, error: '' })).catch((err) => live && setState({ data: null, error: errText(err, 'Could not load cost data') }));
    return () => { live = false; };
  }, [run]);
  return state;
}

export async function downloadCsv(month, setError) {
  try {
    const res = await reportingCostAPI.exportCsv(month);
    saveBlob(res.data, `aws-cost-${month.slice(0, 7)}.csv`);
  } catch (err) { setError(errText(err, 'Could not download the export')); }
}

const Loading = () => <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
const COLUMNS = [['customer_code', 'Customer'], ['cost_gross', 'Gross'], ['cost_net', 'Net'], ['cost_sp_saving', 'SP saved'], ['cost_edp', 'EDP'], ['saving_pct', 'Saving']];

function CustomerDetail({ code, onBack }) {
  const [months, setMonths] = useState(6);
  const [history, setHistory] = useState(false);
  const { data, error } = useLoad(async () => (await reportingCostAPI.customer(code, months)).data, [code, months]);
  const hist = useLoad(async () => (history ? (await reportingCostAPI.regionTrend(code, 12)).data : null), [code, history]);
  const latest = data?.trend?.[data.trend.length - 1] || {};
  const totals = useMemo(() => (data?.trend || []).map((t) => ({ ...t, cost_saving: t.cost_total_saving })), [data]);

  return (
    <Box>
      <Link component="button" underline="hover" onClick={onBack} sx={{ mb: 1 }}>&larr; Back to all customers</Link>
      <Typography variant="h5" gutterBottom>{code} {data && <Typography component="span" color="text.secondary">{data.cust_name}</Typography>}</Typography>
      <ToggleButtonGroup exclusive size="small" value={months} onChange={(e, v) => v && setMonths(v)} aria-label="Months shown" sx={{ mb: 2 }}>
        {[3, 6, 12].map((m) => <ToggleButton key={m} value={m}>{m} months</ToggleButton>)}
      </ToggleButtonGroup>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <Loading />}
      {data && (
        <>
          <KpiGrid>
            <Kpi label="Net cost" value={usd(latest.cost_net)} sub="latest month" color={NET} />
            <Kpi label="Gross cost" value={usd(latest.cost_gross)} sub="before discounts" />
            <Kpi label="SP saving" value={usd(latest.cost_sp_saving)} sub="Savings Plans" color={SP} />
            <Kpi label="EDP saving" value={usd(latest.cost_edp)} sub={`${pct(latest.saving_pct)} total saved`} color={EDP} />
          </KpiGrid>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Monthly trend</Typography>
            <TrendChart data={totals} />
          </Paper>
          <Paper sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>Latest month by region</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Region</TableCell><TableCell sx={{ width: '25%' }} /><TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell><TableCell align="right">SP saved</TableCell><TableCell align="right">EDP</TableCell></TableRow></TableHead>
                <TableBody>
                  {data.regions.filter(hasSpend).map((r) => (
                    <TableRow key={r.region} hover>
                      <TableCell>{r.region}</TableCell><TableCell><CostBar gross={r.cost_gross} net={r.cost_net} edp={r.cost_edp} /></TableCell>
                      <TableCell align="right">{usd(r.cost_gross)}</TableCell><TableCell align="right">{usd(r.cost_net)}</TableCell>
                      <TableCell align="right">{usd(r.cost_sp_saving)}</TableCell><TableCell align="right">{usd(r.cost_edp)}</TableCell>
                    </TableRow>
                  ))}
                  {data.regions.filter(hasSpend).length === 0 && <TableRow><TableCell colSpan={6}>No cost recorded.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Button size="small" onClick={() => setHistory((h) => !h)}>{history ? 'Hide' : 'Show'} 12-month cost by region</Button>
          {history && hist.error && <Alert severity="error" sx={{ mt: 1 }}>{hist.error}</Alert>}
          {history && !hist.data && !hist.error && <Loading />}
          {history && hist.data && (
            <Paper sx={{ mt: 1 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader aria-label="Cost by region by month">
                  <TableHead><TableRow><TableCell>Month</TableCell><TableCell>Region</TableCell><TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell><TableCell align="right">Saving</TableCell></TableRow></TableHead>
                  <TableBody>
                    {hist.data.filter(hasSpend).map((r) => (
                      <TableRow key={`${r.month}-${r.region}`} hover>
                        <TableCell>{monthLabel(r.month)}</TableCell><TableCell>{r.region}</TableCell>
                        <TableCell align="right">{usd(r.cost_gross)}</TableCell><TableCell align="right">{usd(r.cost_net)}</TableCell><TableCell align="right">{pct(r.saving_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}

export default function ReportingCost() {
  return <WithMonths>{(options) => <CostPage options={options} />}</WithMonths>;
}

function CostPage({ options }) {
  const [month, setMonth] = useState(options[0].value);   // the latest month that has data
  const [view, setView] = useState('summary');
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ key: 'cost_gross', dir: 'desc' });
  const [exportError, setExportError] = useState('');
  const { data, error } = useLoad(async () => {
    const [s, a, t] = await Promise.all([reportingCostAPI.accountSummary(month), reportingCostAPI.accountAll(month), reportingCostAPI.trend(12)]);
    return { summary: s.data, customers: a.data, trend: t.data };
  }, [month]);

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = (data?.customers || []).filter((c) => !f || `${c.customer_code} ${c.cust_name}`.toLowerCase().includes(f));
    const sgn = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => (typeof a[sort.key] === 'string' ? a[sort.key].localeCompare(b[sort.key]) : Number(a[sort.key]) - Number(b[sort.key])) * sgn);
  }, [data, filter, sort]);

  if (selected) return <CustomerDetail code={selected} onBack={() => setSelected(null)} />;
  const s = data?.summary;
  const untaggedPct = Number(s?.untagged_pct || 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>AWS Cost</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        AWS spend per customer for a month, from the cost ingest (net after Savings Plans and EDP discounts). Select a customer for its monthly trend and regions.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <MonthSelect value={month} onChange={setMonth} options={options} />
        <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && setView(v)} aria-label="View">
          <ToggleButton value="summary">Customers</ToggleButton><ToggleButton value="trend">12-month trend</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadCsv(month, setExportError)} sx={{ ml: { sm: 'auto' } }}>Export CSV</Button>
      </Box>
      {(error || exportError) && <Alert severity="error" sx={{ mb: 2 }}>{error || exportError}</Alert>}
      {!data && !error && <Loading />}
      {data && (
        <>
          <KpiGrid>
            <Kpi label="Total net cost" value={usd(s.total_net)} sub="after all discounts" color={NET} />
            <Kpi label="Gross (list) cost" value={usd(s.total_gross)} sub="before discounts" />
            <Kpi label="SP savings" value={usd(s.tagged_saving)} sub="Savings Plans (tagged spend)" color={SP} />
            <Kpi label="EDP savings" value={usd(s.tagged_edp)} sub={`${pct(s.saving_pct)} total saved`} color={EDP} />
          </KpiGrid>
          {view === 'trend' ? (
            <Paper sx={{ p: 2 }}><Typography variant="subtitle1" gutterBottom>12-month cost trend, all customers</Typography><TrendChart data={data.trend} /></Paper>
          ) : (
            <>
              <Paper sx={{ p: 2, mb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
                <Box><Typography variant="subtitle2">EDP attribution</Typography><Typography variant="body2" color="text.secondary">Allocated in proportion to each customer's share of gross spend. EDP is an account-level discount applied through the billing profile.</Typography></Box>
                <Box><Typography variant="subtitle2">Tagging coverage</Typography><Typography variant="h5" sx={{ color: untaggedPct > 10 ? 'warning.main' : SP }}>{(100 - untaggedPct).toFixed(1)}%</Typography><Typography variant="caption" color="text.secondary">{untaggedPct.toFixed(1)}% of spend unattributed</Typography></Box>
                <Box><Typography variant="subtitle2">Customers tracked</Typography><Typography variant="h5">{data.customers.filter((c) => c.is_tagged).length.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">with a Client tag</Typography></Box>
              </Paper>
              <Paper sx={{ minWidth: 0 }}>
                <Box sx={{ p: 2, pb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Cost by customer</Typography>
                  <TextField size="small" placeholder="Filter customers" value={filter} onChange={(e) => setFilter(e.target.value)} slotProps={{ htmlInput: { 'aria-label': 'Filter customers' } }} />
                  <Legend items={[['Net', NET], ['SP saving', SP], ['EDP saving', EDP]]} />
                </Box>
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {COLUMNS.map(([key, label], i) => (
                          <React.Fragment key={key}>
                            <TableCell align={i ? 'right' : 'left'} sortDirection={sort.key === key ? sort.dir : false}>
                              <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'desc'} onClick={() => setSort((p) => ({ key, dir: p.key === key && p.dir === 'desc' ? 'asc' : 'desc' }))}>{label}</TableSortLabel>
                            </TableCell>
                            {i === 0 && <TableCell sx={{ width: '20%' }} />}
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((c) => (
                        <TableRow key={c.customer_code} hover={c.is_tagged} onClick={c.is_tagged ? () => setSelected(c.customer_code) : undefined} sx={{ cursor: c.is_tagged ? 'pointer' : 'default' }}>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 500, color: c.is_tagged ? undefined : BAD }}>{c.is_tagged ? c.customer_code : 'UNTAGGED'}</Typography><Typography variant="caption" color="text.secondary">{c.cust_name}</Typography></TableCell>
                          <TableCell><CostBar gross={c.cost_gross} net={c.cost_net} edp={c.cost_edp} untagged={!c.is_tagged} /></TableCell>
                          <TableCell align="right">{usd(c.cost_gross)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{usd(c.cost_net)}</TableCell>
                          <TableCell align="right">{usd(c.cost_sp_saving)}</TableCell>
                          <TableCell align="right">{usd(c.cost_edp)}</TableCell>
                          <TableCell align="right">{pct(c.saving_pct)}</TableCell>
                        </TableRow>
                      ))}
                      {rows.length === 0 && <TableRow><TableCell colSpan={7}>No cost recorded for this month.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </>
      )}
    </Box>
  );
}
